import { Link } from "react-router-dom";

import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { useEntityPeekPrefetchHandlers } from "@/components/hub/EntityPeekHint";
import { cn } from "@/lib/utils";

export function formatWirelessDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function defaultWirelessReportDateRange(): { fromDate: string; toDate: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 3);
  return { fromDate: formatWirelessDateInput(from), toDate: formatWirelessDateInput(to) };
}

export function formatWirelessCell(value: string | number | null | undefined): string {
  if (value == null) return "";
  return String(value);
}

export function WirelessDeviceLink({
  deviceId,
  label,
  className,
}: {
  deviceId: number | null | undefined;
  label: string;
  className?: string;
}) {
  if (deviceId == null || !Number.isFinite(deviceId) || deviceId <= 0) {
    return <span className={className}>{label}</span>;
  }
  return (
    <Link to={`/wireless/devices/${deviceId}`} className={cn("text-brand-700 hover:underline", className)}>
      {label}
    </Link>
  );
}

/** Device serial (portal FCI / label) → manufacturing device lookup with peek. */
export function WirelessDeviceSerialLookupLink({
  serial,
  className,
}: {
  serial: string;
  className?: string;
}) {
  const trimmed = serial.trim();
  const peek =
    trimmed === "" ? undefined : ({ kind: "device" as const, context: { serial: trimmed } } as const);
  const prefetchHandlers = useEntityPeekPrefetchHandlers(peek);
  if (peek == null) {
    return <span className={className}>—</span>;
  }
  return (
    <DetailLinkWithEntityPeek
      peek={peek}
      trigger={
        <Link
          className={cn("font-mono text-brand-700 hover:underline", className)}
          to={`/manufacturing/device-lookup?serial=${encodeURIComponent(trimmed)}`}
          {...prefetchHandlers}
        >
          {trimmed}
        </Link>
      }
    />
  );
}

export function WirelessHubTabs({
  ariaLabel,
  tabs,
  activeKey,
  onChange,
}: {
  ariaLabel: string;
  tabs: Array<{ key: string; label: string }>;
  activeKey: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-slate-200" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={activeKey === tab.key}
          className={cn(
            "-mb-px inline-flex items-center rounded-t-md border border-transparent px-3 py-2 text-sm font-medium",
            activeKey === tab.key
              ? "border-slate-200 border-b-white bg-white text-slate-900"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
          )}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function groupIdsToParam(selected: ReadonlySet<string>): string {
  return [...selected].sort((left, right) => Number(left) - Number(right)).join(",");
}
